import {useEffect, useState} from "react";
import {HomeLink, HomeLinkBase} from "@feature/home/types";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import useActiveCountry from "@hooks/useActiveCountry";
import AuthenticationService from "@services/AuthenticationService";
import {getMenuPermissions} from "@feature/home/lib/utils";

export const useHomeLinks = (all: boolean = false) => {

  // hooks
  const [country, _] = useActiveCountry();
  const countryApiRequest = useCountryAxiosInstance();

  // state
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<number | boolean>(
    // True means creating a new link, number means updating an existing link with that ID
    false
  );
  const [data, setData] = useState<HomeLink[]>([]);

  // utilities
  const userRole = AuthenticationService.getUserRoles();
  const applyMenuFiltering = (links: HomeLink[]) => {
    const menuPermissions = getMenuPermissions(country?.code ?? "");
    return links.filter((l) => {
      const permittedRoles = menuPermissions[l.locale_key];
      if (!permittedRoles)
        return true;  // No restrictions
      if (permittedRoles === "*")
        return true;  // All roles allowed
      return permittedRoles.some((r) => userRole.includes(r));
    });
  }

  // handlers
  const fetch = async () => {
    setLoading(true);
    try {
      const data: HomeLink[] = await countryApiRequest!.get("/home/links" + (all ? "/all" : ""));
      return applyMenuFiltering(data);
    } finally {
      setLoading(false);
    }
  }
  const create = async (link: HomeLinkBase) => {
    setSending(true);
    try {
      const result: HomeLink = await countryApiRequest!.post("/home/links", link);
      setData((prev) => [...prev, result]);
    } finally {
      setSending(false);
    }
  }
  const update = async (id: number, link: HomeLinkBase) => {
    setSending(id);
    try {
      const o: HomeLink = await countryApiRequest!.put(`/home/links/${id}`, link);
      setData((prev) => prev.map((item) => item.id === id ? o : item));
    } finally {
      setSending(false);
    }
  }
  const move = async (id: number, newPosition: number) => {
    setSending(id);
    try {
      await countryApiRequest!.put(`/home/links/${id}/move`, newPosition);
      fetch().then(setData);
    } finally {
      setSending(false);
    }
  }
  const remove = async (id: number) => {
    setSending(id);
    try {
      await countryApiRequest!.delete(`/home/links/${id}`);
      setData((prev) => prev.filter((item) => item.id !== id));
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    // On mount, fetch links
    fetch().then(setData);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    sending,
    data,
    fetch,
    create,
    update,
    move,
    remove,
  };
}